import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { offerApi } from '../api/offerApi';
import type { CreateOfferDTO, UpdateOfferDTO, OfferListResponse } from '../types';

export const offerQueryKeys = {
  all: ['offers'] as const,
  lists: () => [...offerQueryKeys.all, 'list'] as const,
  list: (filters?: any) => [...offerQueryKeys.lists(), filters] as const,
  details: () => [...offerQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...offerQueryKeys.details(), id] as const,
};

export const useOffers = () => {
  return useQuery({
    queryKey: offerQueryKeys.list(),
    queryFn: offerApi.list,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOfferDTO) => offerApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.all });
      toast.success('Offer created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create offer');
    },
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOfferDTO }) =>
      offerApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.all });
      toast.success('Offer updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update offer');
    },
  });
};

export const useUpdateOfferStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      offerApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: offerQueryKeys.list() });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<OfferListResponse>(
        offerQueryKeys.list()
      );

      // Optimistically update to the new value
      queryClient.setQueryData<OfferListResponse>(
        offerQueryKeys.list(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            offers: old.offers.map((offer) =>
              offer.id === id ? { ...offer, is_active } : offer
            ),
            active_offers_count: is_active
              ? old.active_offers_count + 1
              : old.active_offers_count - 1,
            inactive_offers_count: is_active
              ? old.inactive_offers_count - 1
              : old.inactive_offers_count + 1,
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(offerQueryKeys.list(), context.previousData);
      }
      toast.error('Failed to update offer status');
    },
    onSuccess: () => {
      toast.success('Offer status updated');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.all });
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => offerApi.delete(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: offerQueryKeys.list() });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<OfferListResponse>(
        offerQueryKeys.list()
      );

      // Optimistically remove the offer
      queryClient.setQueryData<OfferListResponse>(
        offerQueryKeys.list(),
        (old) => {
          if (!old) return old;
          const deletedOffer = old.offers.find(offer => offer.id === id);
          return {
            ...old,
            offers: old.offers.filter((offer) => offer.id !== id),
            total_count: old.total_count - 1,
            active_offers_count: deletedOffer?.is_active 
              ? old.active_offers_count - 1 
              : old.active_offers_count,
            inactive_offers_count: deletedOffer?.is_active
              ? old.inactive_offers_count
              : old.inactive_offers_count - 1,
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(offerQueryKeys.list(), context.previousData);
      }
      toast.error('Failed to delete offer');
    },
    onSuccess: () => {
      toast.success('Offer deleted successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.all });
    },
  });
};